import React, { useEffect, useState } from "react";
import { Box } from "theme-ui";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { getRevenueTrend, RevenueTrendData } from "api/Dashboard";
import { themevals } from "theme/themevals";

const formatCurrency = (value: number): string => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${Math.round(value / 1000)}k`;
  }
  return `$${value}`;
};

const formatCurrencyFull = (value: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          border: "1px solid",
          borderColor: themevals.colors.inputOutline,
          borderRadius: "6px",
          padding: "8px 12px",
          boxShadow: "0px 4px 12px 0px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Box
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "13px",
            fontWeight: 600,
            color: "base_800",
          }}
        >
          {payload[0].payload.month}
        </Box>
        <Box
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "12px",
            fontWeight: 600,
            color: themevals.colors.info,
          }}
        >
          Revenue: {formatCurrencyFull(payload[0].value)}
        </Box>
      </Box>
    );
  }
  return null;
};

const RevenueTrendChart: React.FC = () => {
  const [trendData, setTrendData] = useState<RevenueTrendData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const data = await getRevenueTrend();
      setTrendData(data);
      setLoading(false);
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: "6px",
          border: "1px solid",
          borderColor: themevals.colors.inputOutline,
          boxShadow: "0px 4px 12px 0px rgba(113, 125, 150, 0.05)",
          flex: "1 1 100%",
          "@media screen and (min-width: 2001px)": {
            flex: "1 1 45%",
          },
          minHeight: "300px",
        }}
      >
        <Box
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "14px",
            fontWeight: 600,
            color: "base_800",
            padding: "16px 20px",
            borderBottom: "1px solid",
            borderColor: themevals.colors.inputOutline,
            minHeight:"76px",
            display:"flex",
            alignItems:"center"
          }}
        >
          Revenue Trend (Last 12 Months)
        </Box>
        <Box sx={{ padding: "20px" }}>
          <Box sx={{ backgroundColor: "#F5F5F5", height: "220px", borderRadius: "4px" }} />
        </Box>
      </Box>
    );
  }

  if (!trendData || trendData.data.length === 0) {
    return (
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: "6px",
          border: "1px solid",
          borderColor: themevals.colors.inputOutline,
          boxShadow: "0px 4px 12px 0px rgba(113, 125, 150, 0.05)",
          flex: "1 1 100%",
          "@media screen and (min-width: 2001px)": {
            flex: "1 1 45%",
          },
          minHeight: "300px",
        }}
      >
        <Box
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "14px",
            fontWeight: 600,
            color: "base_800",
            padding: "16px 20px",
            borderBottom: "1px solid",
            borderColor: themevals.colors.inputOutline,
            minHeight: "76px",
            alignItems:"center"
          }}
        >
          Revenue Trend (Last 12 Months)
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "220px",
            color: "base_500",
            fontFamily: "Poppins, sans-serif",
          }}
        >
          No revenue data available
        </Box>
      </Box>
    );
  }

  const lineColor = themevals.colors.info;

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "6px",
        border: "1px solid",
        borderColor: themevals.colors.inputOutline,
        boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
        flex: "1 1 100%",
        "@media screen and (min-width: 2001px)": {
          flex: "1 1 45%",
        },
        minHeight: "300px",
      }}
    >
      <Box
        sx={{
          fontFamily: "Poppins, sans-serif",
          fontSize: "14px",
          fontWeight: 600,
          color: "base_800",
          padding: "16px 20px",
          borderBottom: "1px solid",
          borderColor: themevals.colors.inputOutline,
          minHeight: "76px",
          alignItems:"center",
          display:"flex"
        }}
      >
        Revenue Trend (Last 12 Months)
      </Box>

      <Box sx={{ padding: "20px" }}>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={trendData.data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid
              strokeDasharray="4 4"
              vertical={false}
              stroke={themevals.colors.inputOutline}
            />
            <XAxis
              dataKey="monthShort"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fill: themevals.colors.base_500, fontFamily: "Poppins, sans-serif" }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tickFormatter={formatCurrency}
              tick={{ fontSize: 11, fill: themevals.colors.base_500, fontFamily: "Poppins, sans-serif" }}
              width={50}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="revenue"
              stroke={lineColor}
              strokeWidth={2}
              dot={{ r: 5, fill: lineColor, strokeWidth: 0 }}
              activeDot={{ r: 7, fill: lineColor, stroke: "#FFFFFF", strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default RevenueTrendChart;
